"use client";
import React, { useState, useCallback, useEffect } from "react";
import IMAGES from "../../../data/assetData";
import { useSelector } from "react-redux";
import Link from "next/link";
import Image from "next/image";
import Button from "@/components/ui/Button";
import { readPartnerOriginSlug } from "@/utils/partner/PartnerContext";

const Dashboard = () => {
  const { userDetails } = useSelector((state) => state.user);
  const [copied, setCopied] = useState(false);
  // userDetails comes from redux-persist (client-only) and is absent during SSR.
  // Render it only after mount so the server HTML and first client render match,
  // otherwise React throws a hydration mismatch on the name/email/mobile.
  const [mounted, setMounted] = useState(false);
  // Keep back/home links inside the partner storefront when the visitor arrived
  // from one (empty string on the global site, so links stay "/"). Set after
  // mount alongside `mounted` so SSR markup stays the global path (no hydration
  // mismatch).
  const [basePath, setBasePath] = useState("");
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot mount flag for SSR hydration parity
    setMounted(true);
    const slug = readPartnerOriginSlug();
    if (slug) setBasePath(`/partner/${slug}`);
  }, []);
  const user = mounted ? userDetails : null;

  const handleCopyReferral = useCallback(() => {
    const code = userDetails?.referralCode;
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [userDetails?.referralCode]);

  const handleInviteFriends = useCallback(() => {
    const code = userDetails?.referralCode;
    const text = `Use my referral code ${code || ""} on Sukuto and get a ride free!`;
    if (navigator.share) {
      navigator.share({ title: "Join Sukuto", text });
    } else {
      navigator.clipboard.writeText(text);
    }
  }, [userDetails?.referralCode]);

  return (
    <main className="w-full bg-bgGray50 min-h-screen">
        <div className="mx-[5%] lg:mx-[216px] pb-12">
          <div className="journey-topbar lg:hidden fixed top-[14px] left-0 right-0 z-20 bg-white/85 backdrop-blur-[12px] py-[14px] px-5 flex items-center gap-2.5">
            <Link href={`${basePath}/`} className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-[background] duration-200 hover:bg-black/5">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M15 19l-7-7 7-7" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <h2 className="text-[15px] font-semibold tracking-[-0.2px] text-heading">Account</h2>
          </div>
          <div className="journey-head hidden lg:flex items-start mt-20 mb-6">
            <Link href={`${basePath}/`} className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-[background] duration-200 hover:bg-black/5 mt-0.5 mr-3">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M15 19l-7-7 7-7" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <div>
              <h1 className="text-lg font-semibold tracking-[-0.2px] text-heading">Account</h1>
              <p className="text-[13px] text-textMuted flex items-center gap-1.5 mt-0.5">
                <Link href={`${basePath}/`} className="hover:text-heading transition-colors">Home</Link>
                <span className="text-textDisabled">/</span>
                <span className="text-heading">Account</span>
              </p>
            </div>
          </div>

          <div className="journey-body grid lg:grid-cols-12 gap-6 mt-20 lg:mt-0">
            <div className="lg:col-span-4">
              <div className="bg-white rounded-3xl border border-black/[0.06] shadow-card-lift overflow-hidden">
                <div className="p-6 text-center">
                  <div className="mb-4">
                    <Image
                      className="mx-auto rounded-full border-[3px] border-lineSoft object-cover"
                      src={IMAGES.avatar}
                      alt="User avatar"
                      width={80}
                      height={80}
                    />
                    <p className="font-semibold text-[15px] tracking-[-0.2px] text-heading mt-3">
                      {user?.name}{" "}
                      {user?.lastName}
                    </p>
                  </div>
                  <div className="mb-4">
                    <Image className="w-full rounded-lg" src={IMAGES.license} alt="Driving license" width={800} height={200} />
                  </div>
                  <div className="space-y-1">
                    {user?.email?.length > 2 && (
                      <p className="text-[13px] text-textMuted">{user?.email}</p>
                    )}
                    {user?.mobile?.length > 2 && (
                      <p className="text-[13px] text-textMuted">{user?.mobile}</p>
                    )}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-[#FFF5F5] to-[#FFF0F0] border-t border-black/[0.06] py-5 px-6">
                  <h4 className="font-semibold text-[15px] tracking-[-0.2px] text-heading mb-1">Referral code</h4>
                  <p className="text-[13px] text-textMuted mb-4">Invite friends and get a ride free!</p>
                  <div className="flex gap-3">
                    <button className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border-[1.5px] border-dashed border-black/20 bg-white font-semibold text-[13px] transition-all duration-200 hover:border-black/40 flex-1" onClick={handleCopyReferral} aria-label="Copy referral code">
                      <Image src={IMAGES.copy} alt="" width={16} height={16} />
                      {copied ? "Copied!" : (user?.referralCode || "—")}
                    </button>
                    <Button variant="dark" size="md" onClick={handleInviteFriends} className="!rounded-xl">Invite Friends</Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-8 grid grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Personal Info — active */}
              <Link href="/user/personal-info" className="block h-full">
                <div className="bg-white rounded-card-lg border border-black/[0.06] shadow-card-sm p-6 max-md:p-4 transition-all duration-[250ms] cursor-pointer flex flex-col gap-3.5 max-md:gap-3 hover:shadow-card-lift hover:bg-redSurface-50 hover:-translate-y-0.5 h-full">
                  <div className="w-12 h-12 max-md:w-10 max-md:h-10 rounded-md max-md:rounded-xl flex items-center justify-center shrink-0 bg-[#EEF2FF]">
                    <Image src={IMAGES.thumbprint} alt="Personal info" width={24} height={24} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[15px] tracking-[-0.2px] text-heading mb-1">Personal Info</h3>
                    <p className="text-[13px] text-textMuted leading-[1.5]">Provide personal details and how we can reach you</p>
                  </div>
                </div>
              </Link>

              {/* Login & Security — coming soon */}
              <div className="bg-white rounded-card-lg border border-black/[0.06] shadow-card-sm p-6 max-md:p-4 flex flex-col gap-3.5 max-md:gap-3 opacity-55 cursor-not-allowed pointer-events-none relative h-full">
                <div className="w-12 h-12 max-md:w-10 max-md:h-10 rounded-md max-md:rounded-xl flex items-center justify-center shrink-0 bg-[#FEF3C7]">
                  <Image src={IMAGES.lockkey} alt="Login and security" width={24} height={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[15px] tracking-[-0.2px] text-heading mb-1">Login &amp; Security</h3>
                  <p className="text-[13px] text-textMuted leading-[1.5]">Update your password and secure your account</p>
                </div>
                <span className="inline-flex items-center justify-center shrink-0 px-2 py-0.5 rounded-card-lg bg-gray-100 text-gray-500 text-[10px] font-semibold tracking-[0.4px] uppercase whitespace-nowrap">Soon</span>
              </div>

              {/* Payments & Payouts — coming soon */}
              <div className="bg-white rounded-card-lg border border-black/[0.06] shadow-card-sm p-6 max-md:p-4 flex flex-col gap-3.5 max-md:gap-3 opacity-55 cursor-not-allowed pointer-events-none relative h-full">
                <div className="w-12 h-12 max-md:w-10 max-md:h-10 rounded-md max-md:rounded-xl flex items-center justify-center shrink-0 bg-[#D1FAE5]">
                  <Image src={IMAGES.flymoney} alt="Payments and payouts" width={24} height={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[15px] tracking-[-0.2px] text-heading mb-1">Payments &amp; Payouts</h3>
                  <p className="text-[13px] text-textMuted leading-[1.5]">Review payments, payouts, coupons and gift cards</p>
                </div>
                <span className="inline-flex items-center justify-center shrink-0 px-2 py-0.5 rounded-card-lg bg-gray-100 text-gray-500 text-[10px] font-semibold tracking-[0.4px] uppercase whitespace-nowrap">Soon</span>
              </div>

              {/* Your Bookings — active */}
              <Link href="/user/booking-list" className="block h-full">
                <div className="bg-white rounded-card-lg border border-black/[0.06] shadow-card-sm p-6 max-md:p-4 transition-all duration-[250ms] cursor-pointer flex flex-col gap-3.5 max-md:gap-3 hover:shadow-card-lift hover:bg-redSurface-50 hover:-translate-y-0.5 h-full">
                  <div className="w-12 h-12 max-md:w-10 max-md:h-10 rounded-md max-md:rounded-xl flex items-center justify-center shrink-0 bg-[#FEE2E2]">
                    <Image src={IMAGES.bwscooty} alt="Your bookings" width={28} height={28} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[15px] tracking-[-0.2px] text-heading mb-1">Your Booking</h3>
                    <p className="text-[13px] text-textMuted leading-[1.5]">Check all your past bookings with us here</p>
                  </div>
                </div>
              </Link>

              {/* Referral & Coupons — coming soon */}
              <div className="bg-white rounded-card-lg border border-black/[0.06] shadow-card-sm p-6 max-md:p-4 flex flex-col gap-3.5 max-md:gap-3 opacity-55 cursor-not-allowed pointer-events-none relative h-full">
                <div className="w-12 h-12 max-md:w-10 max-md:h-10 rounded-md max-md:rounded-xl flex items-center justify-center shrink-0 bg-[#E0E7FF]">
                  <Image src={IMAGES.moneyhand} alt="Referral credits and coupons" width={28} height={28} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[15px] tracking-[-0.2px] text-heading mb-1">Referral &amp; Coupons</h3>
                  <p className="text-[13px] text-textMuted leading-[1.5]">View your referral credit and coupon balance</p>
                </div>
                <span className="inline-flex items-center justify-center shrink-0 px-2 py-0.5 rounded-card-lg bg-gray-100 text-gray-500 text-[10px] font-semibold tracking-[0.4px] uppercase whitespace-nowrap">Soon</span>
              </div>
            </div>
          </div>
        </div>
    </main>
  );
};

export default Dashboard;
